// src/components/dashboard/WelcomeDashboard.jsx
// Streamlined welcome dashboard for new users

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './WelcomeDashboard.css';

const WelcomeDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    // Get user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    } else {
      navigate('/login');
    }

    // Check if user is already verified
    checkVerificationStatus();
  }, []);

  useEffect(() => {
    // Cooldown timer
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const checkVerificationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      
      const response = await axios.get(`${apiUrl}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.user.emailVerified) {
        // User is verified, redirect to full dashboard
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error checking verification:', error);
    }
  };

  const resendVerification = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${apiUrl}/api/auth/resend-verification`,
        { email: user.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVerificationSent(true);
      setResendCooldown(60); // 60 second cooldown
      
      setTimeout(() => setVerificationSent(false), 5000);
    } catch (error) {
      console.error('Error resending verification:', error);
    }
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div className="welcome-dashboard">
      {/* Welcome Hero Section */}
      <div className="welcome-hero">
        <div className="welcome-animation">
          <div className="confetti"></div>
          <div className="confetti"></div>
          <div className="confetti"></div>
        </div>
        
        <h1 className="welcome-title">
          Welcome to SpinoSoftBits, {user.profile?.firstName}! 🎉
        </h1>
        
        <p className="welcome-subtitle">
          Your account has been created successfully. Let's get you started!
        </p>
      </div>

      {/* Email Verification Banner */}
      {!user.emailVerified && (
        <div className="verification-banner">
          <div className="verification-icon">
            <i className="bx bx-envelope"></i>
          </div>
          <div className="verification-content">
            <h3>Verify Your Email to Unlock All Features</h3>
            <p>
              We've sent a verification email to <strong>{user.email}</strong>. 
              Click the link to unlock full access to your dashboard.
            </p>
            <div className="verification-actions">
              <button 
                className="btn-resend"
                onClick={resendVerification}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0 
                  ? `Resend in ${resendCooldown}s` 
                  : 'Resend Verification Email'}
              </button>
              {verificationSent && (
                <span className="success-message">✓ Email sent successfully!</span>
              )}
            </div>
          </div>
        </div>
      )}

 

      {/* Features Available Now vs After Verification */}
      <div className="features-comparison">
        <div className="features-column available">
          <h3>✅ Available Now</h3>
          <ul>
            <li><i className="bx bx-check"></i> View your dashboard</li>
            <li><i className="bx bx-check"></i> Update profile information</li>
            <li><i className="bx bx-check"></i> Browse services</li>
            <li><i className="bx bx-check"></i> Read documentation</li>
            <li><i className="bx bx-check"></i> Contact support</li>
          </ul>
        </div>

        <div className="features-column locked">
          <h3>🔒 After Email Verification</h3>
          <ul>
            <li><i className="bx bx-lock"></i> Create new projects</li>
            <li><i className="bx bx-lock"></i> Make payments</li>
            <li><i className="bx bx-lock"></i> Access API keys</li>
            <li><i className="bx bx-lock"></i> Download invoices</li>
            <li><i className="bx bx-lock"></i> Priority support</li>
          </ul>
        </div>
      </div>

      {/* Streamlined Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid-simple">
          <button 
            className="action-card"
            onClick={() => navigate('/')}
          >
            <i className="bx bx-home"></i>
            <span>Go Back to Home</span>
          </button>
          
          <button 
            className="action-card"
            onClick={() => window.open('https://docs.spinozasoftbits.com', '_blank')}
          >
            <i className="bx bx-book"></i>
            <span>Documentation</span>
          </button>
          
          <button 
            className="action-card"
            onClick={() => navigate('/support')}
          >
            <i className="bx bx-help-circle"></i>
            <span>Get Help</span>
          </button>
        </div>
      </div>

      {/* Support Section */}
      <div className="support-section">
        <h2>Need Help?</h2>
        <p>Our support team is here to help you get started</p>
        <div className="support-options">
          <a href="mailto:support@spinozasoftbits.com" className="support-option">
            <i className="bx bx-envelope"></i>
            support@spinozasoftbits.com
          </a>
          <a href="tel:+19178087320" className="support-option">
            <i className="bx bx-phone"></i>
            (917) 808-7320
          </a>
        </div>
      </div>
    </div>
  );
};

export default WelcomeDashboard;